import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDirective as _resolveDirective, createElementVNode as _createElementVNode, withDirectives as _withDirectives, withKeys as _withKeys, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "labeled-select-with-create" }
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = { class: "icon icon-close align-value" }

import { ref, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { LabeledInput } from '@components/Form/LabeledInput';
import LabeledSelect from '@shell/components/form/LabeledSelect.vue';
import { _CREATE } from '@shell/config/query-params';
import { useI18n } from '@shell/composables/useI18n';
import type { SelectOption } from '@shell/types/components/labeledSelect';
import type { Validator } from '@shell/utils/validators/formRules/index';

/**
 * A LabeledSelect that prepends a "Create new" highlighted option.
 * When the user picks "Create new", the select is replaced by a LabeledInput
 */
type FocusableInput = { focus: () => void };

interface Props {
  value?: string | null;
  options?: SelectOption[];
  name?: string;
  label?: string;
  /** takes precedence over label if set */
  labelKey?: string | null;
  /** Text shown on the "Create new" option in the dropdown */
  createLabel?: string;
  /** Placeholder shown in the new-value input */
  createPlaceholder?: string;
  /** Placeholder shown in the select when not creating */
  placeholder?: string;
  mode?: string;
  loading?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  rules?: Validator[];
  appendToBody?: boolean;
  /**
   * When false, the "Create new" option is not shown.
   * Use this to gate creation on RBAC permissions.
   */
  createAllowed?: boolean;
  requireDirty?: boolean;
  required?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'LabeledSelectWithCreate' },
  __name: 'LabeledSelectWithCreate',
  props: {
    value: { default: null },
    options: { default: () => [] },
    name: { default: undefined },
    label: { default: '' },
    labelKey: { default: null },
    createLabel: { default: undefined },
    createPlaceholder: { default: '' },
    placeholder: { default: '' },
    mode: { default: _CREATE },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    rules: { default: () => [] },
    appendToBody: { type: Boolean, default: false },
    createAllowed: { type: Boolean, default: true },
    requireDirty: { type: Boolean, default: true },
    required: { type: Boolean, default: false }
  },
  emits: ["update:value", "creating", "cancel"],
  setup(__props: any, { emit: __emit }) {



const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

const creating = ref(false);
const newValue = ref('');
const inputRef = ref<FocusableInput | null>(null);

const createLabelText = computed(() => props.createLabel || t('generic.createNew'));

const selectOptions = computed<SelectOption[]>(() => {
  if (!props.createAllowed) {
    return props.options;
  }

  const createEntry: SelectOption = {
    label: createLabelText.value,
    value: '__create__',
    kind:  'highlighted',
  };
  const divider: SelectOption = {
    label:    'divider',
    disabled: true,
    kind:     'divider',
  };

  return [createEntry, divider, ...props.options];
});

function onSelecting(opt: SelectOption | null) {
  // Clearing the select is also a way into create mode, so it has to be allowed too
  if (props.createAllowed && (!opt || opt.value === '__create__')) {
    creating.value = true;
    newValue.value = '';
    emit('creating');
    emit('update:value', '');
    nextTick(() => inputRef.value?.focus());
  }
}

function cancelCreate() {
  creating.value = false;
  newValue.value = '';
  emit('cancel');
}

function onCreateInput(val: string) {
  newValue.value = val;
  emit('update:value', val);
}

function onSelectChange(val: string) {
  if (val !== '__create__') {
    emit('update:value', val);
  }
}

return (_ctx: any,_cache: any) => {
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (creating.value)
      ? (_openBlock(), _createBlock(_unref(LabeledInput), {
          key: 0,
          ref_key: "inputRef",
          ref: inputRef,
          name: __props.name,
          value: newValue.value,
          label: __props.labelKey ? _unref(t)(__props.labelKey) : __props.label,
          placeholder: __props.createPlaceholder,
          mode: __props.mode,
          rules: __props.rules,
          required: __props.required,
          class: "create-input",
          "onUpdate:value": onCreateInput,
          onKeyup: _withKeys(cancelCreate, ["escape"])
        }, {
          suffix: _withCtx(() => [
            _createElementVNode("button", {
              "aria-label": _unref(t)('namespace.cancelCreateAriaLabel'),
              onClick: cancelCreate
            }, [
              _withDirectives(_createElementVNode("i", _hoisted_3, null, 512), [
                [_directive_clean_tooltip, _unref(t)('generic.cancel')]
              ])
            ], 8, _hoisted_2)
          ]),
          _: 1
        }, 8, ["name", "value", "label", "placeholder", "mode", "rules", "required"]))
      : (_openBlock(), _createBlock(LabeledSelect, {
          key: 1,
          name: __props.name,
          value: __props.value ?? undefined,
          label: __props.labelKey ? _unref(t)(__props.labelKey) : __props.label,
          placeholder: __props.placeholder,
          options: selectOptions.value,
          mode: __props.mode,
          loading: __props.loading,
          disabled: __props.disabled,
          clearable: __props.clearable,
          rules: __props.rules,
          "append-to-body": __props.appendToBody,
          "require-dirty": __props.requireDirty,
          required: __props.required,
          searchable: true,
          onSelecting: onSelecting,
          "onUpdate:value": onSelectChange
        }, null, 8, ["name", "value", "label", "placeholder", "options", "mode", "loading", "disabled", "clearable", "rules", "append-to-body", "require-dirty", "required"]))
  ]))
}
}

})